import { useEffect, useState } from "react";
import TextField from "@/components/ui/Textfield";
import { JSX } from "react";
import Dropdown from "@/components/ui/DropDown";
import { useAdCategoryStore } from "../store/adCategoryStore";
import { useShallow } from "zustand/react/shallow";
import { FaTrash } from "react-icons/fa";
import Button from "@/components/ui/Button";
import { PlusIcon } from "lucide-react";
import FileUpload from "./FileUpload";
import useGlobalStore from "@/store/globalStore";
import Spinner from "@/components/ui/Spinner";
import { useNavigate, useSearchParams } from "react-router-dom";
import ToggleSwitch from "@/components/ui/Switch";

const fieldTypeOptions = [
  { title: "Text", value: "text" },
  { title: "Number", value: "number" },
  { title: "Dropdown", value: "dropdown" },
  { title: "Date", value: "date" },
  { title: "Checkbox", value: "checkbox" },
  { title: "Ratio", value: "ratio" },
  { title: "File Upload", value: "file_upload" },
];

export const settingsOptions = [
  { title: "Default", value: "single_publisher" },
  { title: "User Defined", value: "user_defined" },
  { title: "Multiple", value: "multi_publisher" },
];

/**
 * UI-only sentinel for the 4th setting option. This value NEVER leaves the
 * frontend: the store decodes it into `settings: "multi_publisher"` plus
 * `restricted: true`, which is exactly the payload the backend already
 * accepts (`value_type` + `visibility_metadata.restricted`). Keeping it out of
 * the wire format means no migration, no enum widening, and every existing
 * `value_type === "multi_publisher"` consumer keeps working untouched.
 */
export const RESTRICTED_MULTIPLE = "multi_publisher_restricted";

/**
 * Options for the per-group parameter dropdown only.
 *
 * Deliberately separate from `settingsOptions`, which is still the 3-option
 * list used by the custom-parameter dropdown. Custom parameters send their mode
 * as `setting`, validated server-side against
 * `in:single_publisher,multi_publisher,multiple,user_defined` — feeding the
 * sentinel into that field would fail validation.
 */
export const parameterSettingsOptions = [
  ...settingsOptions,
  { title: "Multiple (Restricted)", value: RESTRICTED_MULTIPLE },
];

/** Collapse the stored (settings, restricted) pair into a dropdown value. */
export function toParameterMode(
  settings?: string,
  restricted?: boolean,
): string {
  return settings === "multi_publisher" && restricted === true
    ? RESTRICTED_MULTIPLE
    : (settings ?? "");
}

/** Human label for a stored (settings, restricted) pair. */
export function parameterSettingLabel(
  settings?: string,
  restricted?: boolean,
): string {
  const mode = toParameterMode(settings, restricted);

  return (
    parameterSettingsOptions.find((o) => o.value === mode)?.title ??
    settings ??
    ""
  );
}

function getCustomInput(param, value, onChange) {
  switch (param.type) {
    case "number":
    case "text":
    case "date":
      return (
        <TextField
          type={param.type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-1/3"
          disabled={param.setting == "user_defined" ? true : false}
        />
      );
    case "ratio":
      return (
        <TextField
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Height X Width"
          className="w-1/3"
          disabled={param.setting == "user_defined" ? true : false}
        />
      );
    case "file_upload":
      return (
        <FileUpload
          value={value}
          onChange={onChange}
          disabled={param.setting === "user_defined"}
          className="w-1/3"
        />
      );
    default:
      return null;
  }
}

export default function CreateAdCategoriesStepFour(): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const isViewMode = searchParams.get("viewAdType") === "true";
  const viewAdTypeIndex = parseInt(searchParams.get("adTypeIndex") || "0");
  const viewAdInputIndex = parseInt(searchParams.get("adInputIndex") || "0");

  const {
    adTypes,
    parametersInput,
    updateParameterInput,
    addParameterInput,
    removeParameterInput,
    customParameters,
    addCustomParameter,
    updateCustomParameter,
    removeCustomParameter,
    addDropdownOption,
    updateDropdownOption,
    removeDropdownOption,
    setCustomParamSetting,
    setCustomParamValue,
    removeCustomParamValue,
    addCustomParamValue,
    fetchParametersData,
    parametersData,
    toggleParameterEnabled,
    setParameterMode,
    toggleCustomParameterEnabled,
  } = useAdCategoryStore(
    useShallow((state) => ({
      adTypes: state.adTypes,
      parametersInput: state.parametersInput,
      updateParameterInput: state.updateParameterInput,
      addParameterInput: state.addParameterInput,
      removeParameterInput: state.removeParameterInput,
      customParameters: state.customParameters,
      addCustomParameter: state.addCustomParameter,
      updateCustomParameter: state.updateCustomParameter,
      removeCustomParameter: state.removeCustomParameter,
      addDropdownOption: state.addDropdownOption,
      updateDropdownOption: state.updateDropdownOption,
      removeDropdownOption: state.removeDropdownOption,
      setCustomParamSetting: state.setCustomParamSetting,
      setCustomParamValue: state.setCustomParamValue,
      removeCustomParamValue: state.removeCustomParamValue,
      addCustomParamValue: state.addCustomParamValue,
      fetchParametersData: state.fetchParametersData,
      parametersData: state.parametersData,
      toggleParameterEnabled: state.toggleParameterEnabled,
      setParameterMode: state.setParameterMode,
      toggleCustomParameterEnabled: state.toggleCustomParameterEnabled,
    })),
  );

  const { setError, showSuccess } = useGlobalStore(
    useShallow((state) => ({
      setError: state.setError,
      showSuccess: state.showSuccess,
    })),
  );

  // Filter adTypes based on view mode
  const displayAdTypes =
    isViewMode && adTypes[viewAdTypeIndex]
      ? [
          {
            ...adTypes[viewAdTypeIndex],
            adInputs: [adTypes[viewAdTypeIndex].adInputs[viewAdInputIndex]],
          },
        ]
      : adTypes;

  const handleRemoveCustomParameter = async (key: string) => {
    const param = customParameters.find((p) => p.key === key);
    try {
      await removeCustomParameter(key);
      if (param?.id) {
        showSuccess("Custom parameter deleted successfully");
      }
    } catch (error: any) {
      setError(error.message ?? "Failed to delete custom parameter");
    }
  };

  const handleBackToAdTypes = () => {
    const id = searchParams.get("id");
    const { resetValue } = useAdCategoryStore.getState();
    resetValue();

    if (isViewMode && id) {
      navigate(`/ad-categories/publishers?id=${id}`);
    } else {
      searchParams.delete("viewAdType");
      searchParams.delete("adTypeIndex");
      searchParams.delete("adInputIndex");
      searchParams.set("step", "3");
      setSearchParams(searchParams);
    }
  };

  const handleSaveParameters = async () => {
    const { AdCategoryValidation } = await import("../validation");
    const state = useAdCategoryStore.getState();

    const currentAdType = state.adTypes[viewAdTypeIndex];
    const currentAdInput = currentAdType?.adInputs[viewAdInputIndex];
    const baseKeyPrefix =
      currentAdType && currentAdInput
        ? `${currentAdType.publisher || "null"}_${currentAdInput.name}`
        : undefined;

    const validation = AdCategoryValidation.validateStepFour(
      state,
      baseKeyPrefix,
    );

    if (!validation.isValid) {
      const errorMessages = AdCategoryValidation.formatErrors(
        validation.errors,
      );
      setError(
        errorMessages[0] ||
          "Please complete all required parameters before saving",
      );
      return;
    }

    const id = searchParams.get("id");
    if (id) {
      try {
        const { updateDataConvertToAPIFormat } = await import("../helper");
        const requestData = updateDataConvertToAPIFormat(state);

        const { updateAdCategory } = useAdCategoryStore.getState();
        await updateAdCategory("4", id, requestData);

        showSuccess("Parameters saved successfully");

        const { resetValue } = useAdCategoryStore.getState();
        resetValue();

        if (isViewMode) {
          navigate(`/ad-categories/publishers?id=${id}`);
        } else {
          navigate("/ad-categories");
        }
      } catch (error: any) {
        setError(error.message ?? "Failed to save parameters");
      }
    }
  };

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await fetchParametersData();
    } catch (e) {
      setError(e?.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initializeStep = async () => {
      await fetchData();
      // Always call generateParametersInput so that ad inputs without saved
      // parameters (e.g. a newly added ad type) still get their rows generated.
      // The function merges existing params with any missing ones, so already-
      // saved values are preserved.
      const { generateParametersInput } = useAdCategoryStore.getState();
      generateParametersInput();
    };

    initializeStep();
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center w-screen h-screen">
        <Spinner size={30} />
      </div>
    );
  }

  const currentAdTypeName =
    isViewMode && displayAdTypes[0]?.adInputs[0]
      ? displayAdTypes[0].adInputs[0].name
      : adTypes[0]?.adInputs[0]?.name || "[Ad Type]";

  return (
    <div className="mt-6">
      <div className="font-bold text-xl mb-6 flex items-center gap-2">
        {isViewMode && (
          <button
            onClick={handleBackToAdTypes}
            className="text-pink-500 hover:text-pink-700"
            aria-label="Back to Ad Types"
          >
            ←
          </button>
        )}
        <span>Assign Parameters for {currentAdTypeName}</span>
      </div>
      <hr className="border-[#EA33C0] mb-6" />
      <div>
        {displayAdTypes.map((adType, adTypeIndex) => (
          <div key={adTypeIndex} className={adTypeIndex != 0 && "mt-5"}>
            {adType.adInputs.map((adInput, adInputIndex) => {
              const baseKey = `${adType.publisher}_${adInput.name}`;
              return (
                <div
                  key={adInputIndex}
                  className={adInputIndex !== 0 ? "mt-5" : undefined}
                >
                  {(parametersData[adInput.mediaType] || []).map((group) => {
                    const parameterKey = `${baseKey}_${group.key}`;
                    // The fallback carries `restricted` so both branches of the
                    // union expose the field; without it the property is not
                    // readable off `paramObj`.
                    const paramObj = parametersInput.find(
                      (p) => p.key === parameterKey,
                    ) || {
                      settings: "single_publisher",
                      inputs: [{}],
                      enabled: true,
                      restricted: false,
                    };
                    const { settings, inputs, enabled, restricted } = paramObj;
                    const isEnabled = enabled !== false;
                    return (
                      <div className="mt-6" key={group.key}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="text-sm font-semibold">
                            {group.label}
                          </div>
                        </div>
                        <hr className="mt-2 border-gray-300 mb-3" />
                        <div>
                          {inputs.map((input, inputIndex) => (
                            <div
                              key={inputIndex}
                              className="flex items-start justify-between gap-6 mb-4"
                            >
                              {/* Left group: all fields */}
                              <div className="flex-1 flex gap-6 overflow-x-auto">
                                {/* Wider than the other columns so the longest
                                    option, "Multiple (Restricted)", is not
                                    truncated by the Dropdown's `truncate`. */}
                                <div className="min-w-[200px]">
                                  {inputIndex === 0 && (
                                    <>
                                      <div className="text-gray-400 text-sm mb-1">
                                        Setting
                                      </div>
                                      {/* "Multiple (Restricted)" replaces the
                                          former Restricted toggle. The store
                                          decodes it back into multi_publisher +
                                          restricted, so the payload is
                                          unchanged. */}
                                      <Dropdown
                                        options={parameterSettingsOptions}
                                        selected={toParameterMode(
                                          settings,
                                          restricted,
                                        )}
                                        setSelected={(opt) =>
                                          setParameterMode(
                                            parameterKey,
                                            opt.toString(),
                                          )
                                        }
                                      />
                                    </>
                                  )}
                                </div>
                                {group.fields.map((field) => {
                                  const value = input[field.key] ?? "";
                                  return (
                                    <div
                                      key={field.key}
                                      className="min-w-[160px]"
                                    >
                                      <div className="text-gray-400 text-sm mb-1">
                                        {field.label}
                                      </div>
                                      {field.type === "dropdown" ? (
                                        <Dropdown
                                          options={field.options ?? []}
                                          selected={value}
                                          setSelected={(opt) =>
                                            updateParameterInput(
                                              parameterKey,
                                              field.key,
                                              opt.toString(),
                                              inputIndex,
                                            )
                                          }
                                          disabled={settings === "user_defined"}
                                        />
                                      ) : (
                                        <TextField
                                          type={field.type}
                                          placeholder={field.label}
                                          value={value}
                                          onChange={(e) =>
                                            updateParameterInput(
                                              parameterKey,
                                              field.key,
                                              e.target.value,
                                              inputIndex,
                                            )
                                          }
                                          disabled={settings === "user_defined"}
                                          className="w-full"
                                        />
                                      )}
                                    </div>
                                  );
                                })}
                                {settings === "multi_publisher" && (
                                  <div className="flex-shrink-0 flex mt-4">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeParameterInput(
                                          parameterKey,
                                          inputIndex,
                                        )
                                      }
                                      className="text-red-500 hover:text-red-700"
                                      title="Remove this entry"
                                    >
                                      <FaTrash />
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Right group: toggle (always rendered to keep rows even) */}
                              <div className="flex-shrink-0 pb-1 w-12">
                                {inputIndex === 0 && (
                                  <ToggleSwitch
                                    enabled={isEnabled}
                                    setEnabled={() =>
                                      toggleParameterEnabled(parameterKey)
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        {settings === "multi_publisher" && (
                          <Button
                            type="secondary"
                            leadingIcon={<PlusIcon />}
                            onClick={() => addParameterInput(parameterKey)}
                          >
                            Add More
                          </Button>
                        )}
                      </div>
                    );
                  })}
                  <div className="mt-10">
                    <hr className="mt-10 border-1 border-gray-300 mb-8" />
                    {/* Custom Parameters Section */}
                    {customParameters
                      .filter(
                        (e) =>
                          e.key.split("_")[0] + "_" + e.key.split("_")[1] ==
                          baseKey,
                      )
                      .map((param, customParameterIndex) => {
                        const isCustomEnabled = param.enabled !== false;
                        return (
                          <div key={param.key} className="mb-6">
                            <div className="flex items-center justify-between mb-1">
                              <div className="text-sm text-black">
                                Custom Parameter: {customParameterIndex + 1}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">
                                  {isCustomEnabled ? "Enabled" : "Disabled"}
                                </span>
                                <ToggleSwitch
                                  enabled={isCustomEnabled}
                                  setEnabled={() =>
                                    toggleCustomParameterEnabled(param.key)
                                  }
                                />
                              </div>
                            </div>
                            <div className="flex flex-col md:flex-row items-center gap-4 mb-2">
                              <TextField
                                type="text"
                                placeholder="Field Name"
                                value={param.name}
                                onChange={(e) =>
                                  updateCustomParameter(
                                    param.key,
                                    "name",
                                    e.target.value,
                                  )
                                }
                                className="w-full md:w-1/3"
                              />
                              <Dropdown
                                options={fieldTypeOptions}
                                selected={param.type ?? ""}
                                setSelected={(val) =>
                                  updateCustomParameter(
                                    param.key,
                                    "type",
                                    val.toString(),
                                  )
                                }
                                className="w-full md:w-1/3"
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveCustomParameter(param.key)
                                }
                                className="text-red-500"
                                title="Remove this parameter"
                              >
                                <FaTrash />
                              </button>
                            </div>
                            <div className="flex items-center gap-2 mt-3">
                              <Dropdown
                                options={settingsOptions}
                                selected={param.setting ?? ""}
                                setSelected={(val) =>
                                  setCustomParamSetting(param.key, val as any)
                                }
                                className="w-40"
                              />
                            </div>
                            <div className="mt-2">
                              {param.setting === "multi_publisher" &&
                              param.type != "dropdown" &&
                              param.type != "checkbox"
                                ? (param.values || [""]).map((val, idx) => (
                                    <div
                                      key={idx}
                                      className="flex items-center gap-2 mb-2"
                                    >
                                      {getCustomInput(param, val, (v) =>
                                        setCustomParamValue(param.key, v, idx),
                                      )}
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeCustomParamValue(param.key, idx)
                                        }
                                        className="text-red-400 hover:text-red-700"
                                        title="Remove"
                                      >
                                        <FaTrash />
                                      </button>
                                    </div>
                                  ))
                                : getCustomInput(
                                    param,
                                    (param.values || [""])[0] || "",
                                    (v) => setCustomParamValue(param.key, v, 0),
                                  )}
                              {param.setting === "multi_publisher" &&
                                param.type != "dropdown" &&
                                param.type != "checkbox" && (
                                  <Button
                                    type="secondary"
                                    onClick={() =>
                                      addCustomParamValue(param.key)
                                    }
                                  >
                                    Add Value
                                  </Button>
                                )}
                            </div>
                            {param.type === "dropdown" && (
                              <div>
                                {param.dropdownOptions?.map((opt, idx) => (
                                  <div
                                    key={idx}
                                    className="flex items-center gap-2 mb-2"
                                  >
                                    <TextField
                                      type="text"
                                      value={opt}
                                      onChange={(e) =>
                                        updateDropdownOption(
                                          param.key,
                                          idx,
                                          e.target.value,
                                        )
                                      }
                                      placeholder={`Option ${idx + 1}`}
                                      className="w-1/3"
                                    />
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeDropdownOption(param.key, idx)
                                      }
                                      className="text-red-400"
                                    >
                                      <FaTrash />
                                    </button>
                                  </div>
                                ))}
                                <Button
                                  type="secondary"
                                  onClick={() => addDropdownOption(param.key)}
                                >
                                  Add Option
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}

                    <Button
                      type="secondary"
                      leadingIcon={<PlusIcon />}
                      onClick={() => addCustomParameter(baseKey)}
                    >
                      Add Custom Parameter
                    </Button>
                  </div>

                  {adInputIndex !== adType.adInputs.length - 1 && (
                    <hr className="mt-10 border-1 border-gray-300 mb-3" />
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      <hr className="mt-10 border-1 border-gray-300 mb-2" />
      <div className="flex gap-3">
        <Button
          type="secondary"
          text="Previous"
          className="mt-4 px-9"
          onClick={handleBackToAdTypes}
        />
        <Button
          type="primary"
          text="Save"
          className="mt-4 px-9"
          onClick={handleSaveParameters}
        />
      </div>
    </div>
  );
}
